/**
 * TagDropdownContent Component
 *
 * Multi-select tag picker with search, create-new, confirm and optional remove-all actions.
 * Selection is local until the user confirms — onChange is only called on confirm.
 *
 * @example
 * // Actionbar variant (no remove button)
 * <TagDropdownContent
 *   selectedIds={selectedTagIds}
 *   onChange={(ids) => setSelectedTagIds(ids)}
 *   onClose={() => setOpen(false)}
 * />
 *
 * @example
 * // Badge variant (with remove-all button)
 * <TagDropdownContent
 *   variant="badge"
 *   selectedIds={task.tagIds}
 *   onChange={(ids) => updateTask({ tagIds: ids })}
 *   onClose={() => setOpen(false)}
 *   onRemoveAll={() => updateTask({ tagIds: [] })}
 * />
 */
import React, { useState, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { Minus, Plus, Trash2 } from 'lucide-react'
import { TagIcon, MultiSelectCheckIcon } from '@/src/renderer/components/icons'
import { Button } from '@/src/renderer/components/ui/Button'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/src/renderer/components/ui/tooltip'
import { useTags } from '../../hooks/useTags'
import type { TagDropdownContentProps } from './TagDropdown.types'
import './TagDropdown.scss'

export const TagDropdownContent: React.FC<TagDropdownContentProps> = ({
  selectedIds,
  onChange,
  onClose,
  onRemoveAll,
  variant = 'actionbar',
  highlightId,
}) => {
  const { t } = useTranslation()
  const { tags, createTag } = useTags()
  const [query, setQuery] = useState('')
  const [localSelected, setLocalSelected] = useState<string[]>(() => [...selectedIds])
  const inputRef = useRef<HTMLInputElement>(null)
  const isCreating = useRef(false)

  const hasChanges = localSelected.length !== selectedIds.length
    || localSelected.some(id => !selectedIds.includes(id))

  const availableTags = variant === 'add'
    ? tags.filter((tag) => !selectedIds.includes(tag.id))
    : tags

  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(query.toLowerCase()),
  )

  const exactMatch = tags.some(
    (tag) => tag.name.toLowerCase() === query.trim().toLowerCase(),
  )
  const showCreateOption = query.trim() !== '' && !exactMatch

  const toggleTag = (id: string) => {
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const handleCreate = async () => {
    const trimmed = query.trim()
    if (!trimmed || isCreating.current) return
    isCreating.current = true
    try {
      const newTag = await createTag(trimmed)
      if (variant === 'add') {
        onChange([...selectedIds, newTag.id])
        onClose()
      } else {
        setLocalSelected((prev) => [...prev, newTag.id])
        setQuery('')
      }
    } catch (err) {
      console.error('Failed to create tag:', err)
      toast.error(t('taskCreation.tagCreateFailed'))
    } finally {
      isCreating.current = false
    }
  }

  const handleConfirm = () => {
    onClose()
    setTimeout(() => onChange(localSelected), 100)
  }

  const handleRemoveAll = () => {
    onClose()
    setTimeout(() => onRemoveAll?.(), 100)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.isComposing) return
    if (e.key === 'Enter') {
      if (showCreateOption) {
        handleCreate()
      } else {
        handleConfirm()
      }
    }
  }

  const dynamicPlaceholder =
    variant === 'add'
      ? (availableTags.length === 0
          ? t('taskCreation.tagCreatePlaceholder')
          : t('taskCreation.tagPlaceholder'))
      : localSelected.length === 0
        ? t('taskCreation.tagPlaceholder')
        : localSelected
            .flatMap((id) => {
              const tag = tags.find((t) => t.id === id)
              return tag ? [`#${tag.name}`] : []
            })
            .join(' ')

  const makePointerDownHandler = (callback: () => void) => (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const btn = e.currentTarget
    let left = false
    const onUp = () => {
      cleanup()
      if (!left) callback()
    }
    const onLeave = () => {
      left = true
      cleanup()
    }
    const cleanup = () => {
      btn.removeEventListener('pointerup', onUp)
      btn.removeEventListener('pointerleave', onLeave)
    }
    btn.addEventListener('pointerup', onUp)
    btn.addEventListener('pointerleave', onLeave)
  }

  return (
    <div className="tag-dropdown" data-variant={variant}>
      <div className="tag-dropdown__search-wrapper">
        <div className="tag-dropdown__search">
          <TagIcon size={16} />
          <input
            ref={inputRef}
            className="tag-dropdown__input"
            placeholder={dynamicPlaceholder}
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      <div className="tag-dropdown__list">
        {filteredTags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            className={`context-menu-content__item tag-dropdown__item${(highlightId != null ? tag.id === highlightId : localSelected.includes(tag.id)) ? ' tag-dropdown__item--passive-selected' : ''}`}
            onClick={() => {
              if (variant === 'add' && filteredTags.length <= 1) {
                onChange([...selectedIds, tag.id])
                onClose()
              } else {
                toggleTag(tag.id)
              }
            }}
          >
            <TagIcon size={16} className="tag-dropdown__item-icon" />
            {tag.name}
            <div className="tag-dropdown__item-right">
              {localSelected.includes(tag.id) ? (
                <>
                  <MultiSelectCheckIcon size={16} className="tag-dropdown__check tag-dropdown__check--default" />
                  <Minus size={16} className="tag-dropdown__check tag-dropdown__check--hover" />
                </>
              ) : (
                <Plus size={16} className="tag-dropdown__add-icon" />
              )}
            </div>
          </button>
        ))}

        {showCreateOption && (
          <button
            type="button"
            className="context-menu-content__item tag-dropdown__item tag-dropdown__item--create"
            onClick={handleCreate}
          >
            <Plus size={14} />
            {t('taskCreation.tagCreateNew', { name: query })}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {!(showCreateOption && filteredTags.length === 0) && !(variant === 'add' && filteredTags.length <= 1) && (variant === 'badge' || (variant === 'add' ? localSelected.some(id => !selectedIds.includes(id)) : localSelected.length > 0)) && (
        <motion.div
          key="action-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="context-menu-content__separator" />
          <div className="tag-dropdown__action-bar">
            <AnimatePresence initial={false} mode="wait">
              {variant === 'badge' && onRemoveAll && !hasChanges ? (
                <motion.button
                  key="remove-full"
                  type="button"
                  className="tag-dropdown__remove-btn tag-dropdown__remove-btn--full"
                  onPointerDown={makePointerDownHandler(handleRemoveAll)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                >
                  <Trash2 size={16} />
                  {t('taskCreation.tagRemoveAll')}
                </motion.button>
              ) : (
                <motion.div
                  key="actions"
                  className="tag-dropdown__action-bar-inner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                >
                  {variant === 'badge' && onRemoveAll && (
                    <TooltipProvider delayDuration={400}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" icon={<Trash2 size={16} />} onClick={handleRemoveAll} />
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {t('taskCreation.tagRemoveAll')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    onClick={handleConfirm}
                    shortcut={{ variant: 'return' }}
                    disabled={variant !== 'badge' && localSelected.length === 0}
                  >
                    {t('taskCreation.tagConfirm')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
